import getOrderBumpOffers, {
  filterOffersByRules,
  isOfferCompatibleWithSelectedPrice,
  resolveCompatSelectedPrice,
} from './getOrderBumpOffers';
import hasStock from './hasStock';

// What ticking a bump selects. A placement that allows a quantity opens here too
// (the stepper is how the buyer moves off it), and one that does not is pinned
// here by the server as well (assertValidOrderBumpSubmission).
export const BUMP_QUANTITY = 1;

// The submission schema's own ceiling (OrderBumpSubmissionValidationSchema), so
// a stepper on an untracked bump cannot climb past what the server accepts.
const MAX_BUMP_QUANTITY = 999;

/**
 * The most of a bump a buyer may take: one unless the placement offers a
 * stepper, and never more of a tracked-stock bump than is left: the same two
 * ceilings the server refuses to cross (assertValidOrderBumpSubmission).
 */
export function getMaxBumpQuantity(offer) {
  if (!offer.allowQuantity) return BUMP_QUANTITY;

  return offer.product?.hasUnlimitedStock ? MAX_BUMP_QUANTITY : (offer.product?.stock ?? 0);
}

/**
 * The offers a buyer may END UP BUYING: everything except `hidden`.
 *
 * This is the set form state follows, and the split matters: `hidden` is
 * display-only, so a hidden bump keeps its selection and is still submitted,
 * exactly as getSelectedVariants keeps a hidden variant (it checks
 * checkIsVariantEnabled, never checkIsVariantVisible). That is what makes
 * "preselect it, hide it, fill it from the URL" work. The other three guards
 * are real ineligibility (a bump the primary's selected price cannot carry, a
 * rule the selection fails, a sold-out product), so those DO clear it.
 */
export default function getEligibleOrderBumpOffers({
  page,
  product,
  selectedPrice,
  ticketGroups,
  orderBumps,
}) {
  const offers = getOrderBumpOffers(page, product, page?.orderBumpProducts);
  if (!offers.length) return [];

  const compatSelectedPrice = resolveCompatSelectedPrice(product, selectedPrice);

  // The seller's rule reads the RAW selection, not the compat fallback - see
  // isOfferShownForSelectedPrice. On an event it reads the tickets, and a rule
  // on other bumps reads which of them are ticked.
  return filterOffersByRules(offers, {
    page,
    selectedPrice,
    ticketGroups,
    orderBumps,
    isAvailable: offer =>
      isOfferCompatibleWithSelectedPrice(offer, compatSelectedPrice) && hasStock(offer.product),
  });
}

/**
 * Sold-out offers the buyer would otherwise be shown. They are displayed, marked
 * sold out, but stay out of the eligible set so they are never ticked or charged.
 */
export function getSoldOutOrderBumpOffers({
  page,
  product,
  selectedPrice,
  ticketGroups,
  orderBumps,
}) {
  const offers = getOrderBumpOffers(page, product, page?.orderBumpProducts);
  if (!offers.length) return [];

  const compatSelectedPrice = resolveCompatSelectedPrice(product, selectedPrice);

  // Only the candidate is let past the stock check: the bumps its rule watches are
  // judged as the eligible set judges them, so it shows exactly where it would with stock.
  return offers.filter(candidate => {
    if (hasStock(candidate.product)) return false;

    const candidateId = String(candidate.productId);
    return filterOffersByRules(offers, {
      page,
      selectedPrice,
      ticketGroups,
      orderBumps,
      isAvailable: offer =>
        isOfferCompatibleWithSelectedPrice(offer, compatSelectedPrice) &&
        (String(offer.productId) === candidateId || hasStock(offer.product)),
    }).some(offer => String(offer.productId) === candidateId);
  });
}
